import { getCSS, remCSS } from '@gimlite/common';
import classNames from 'classnames';
import React, { memo, useContext, useState } from 'react';
import { ColorType } from '../../types/component.type';
import { Event } from '../event/event.component';
import { FormItemContext } from '../form/form.component';
import { Icon } from '../icon/icon.component';
import './inputPassword.component.scss';

const sizeMapper = {
  small: {
    padding: `${remCSS(8)} ${remCSS(16)}`,
  },
  medium: {
    padding: `${remCSS(16)} ${remCSS(16)}`,
  },
} as const satisfies Record<string, { padding: string }>;

const borderStatusMapper = {
  error: 'alert_error',
  success: 'alert_success',
} as const satisfies Record<string, ColorType>;

export declare namespace InputPasswordType {
  type Props = {
    className?: string | null;
    placeholder?: string | null;
    onChange?: (value: string | null) => void;
    onBlur?: () => void;
    size?: 'small' | 'medium';
    value?: string | null;
    defaultValue?: string | null;
    disabled?: boolean;
    name?: string;
  };
}

export const InputPassword = memo(
  React.forwardRef<HTMLInputElement, InputPasswordType.Props>(
    (
      {
        className,
        placeholder,
        onChange,
        size = 'medium',
        value,
        defaultValue,
        onBlur,
        disabled,
        name,
      }: InputPasswordType.Props,
      ref,
    ) => {
      const formItemContext = useContext(FormItemContext);
      const id = React.useId();
      const [isLoading, setIsLoading] = useState<boolean>(false);
      const [isVisible, setIsVisible] = useState<boolean>(false);

      const isDisabled = disabled || isLoading;

      return (
        <div className="inputPassword">
          <label
            htmlFor={id}
            className={classNames(
              'inputPassword__wrapper',
              {
                'inputPassword__wrapper--disabled': isDisabled,
              },
              className,
            )}
            style={{
              padding: sizeMapper[size].padding,
              borderColor: formItemContext.borderStatus
                ? getCSS(
                    `--color-${borderStatusMapper[formItemContext.borderStatus]}`,
                  )
                : getCSS('--color-deep_indigo_10'),
            }}
          >
            <input
              id={id}
              name={name}
              defaultValue={defaultValue || ''}
              value={value || ''}
              disabled={isDisabled}
              className={classNames('inputPassword__wrapper__native', {
                'inputPassword__wrapper__native--hidden': !isVisible,
              })}
              placeholder={placeholder || ''}
              onChange={(e) => {
                const value = e.target.value;
                onChange?.(value || null);
              }}
              onBlur={onBlur}
              type={isVisible ? 'text' : 'password'}
              ref={ref}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
            />
            <Event onClick={() => !isDisabled && setIsVisible((prev) => !prev)}>
              <Icon
                variant={isVisible ? 'eye' : 'eye_hide'}
                color={'deep_indigo'}
              />
            </Event>
          </label>
        </div>
      );
    },
  ),
);
